import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Modal, Tooltip } from 'antd';
import {
  useHasRelatedFlat,
  useRelatedRoomIds,
  useRelatedRoomsActions,
} from './provider';
import ColListWrapper from '../../../shared/ui/ColListWrapper';
import BaseCheckbox from '../../../shared/ui/BaseCheckbox';
import { createHouseAddress } from '../../../shared/packages/address';
import { actionCreators, selectors, sliceKeys } from '../../../model';
import { FLAT_TYPE_ID } from '../../../shared/configs/address';
import BaseRoomItem from '../../../shared/ui/BaseRoomItem';

const { selectRelatedRoomIds, createIsRoomExcludedSelector } =
  selectors[sliceKeys.houses];
const { roomExcludeClicked } = actionCreators[sliceKeys.houses];

const MemoizedRelatedItem = memo(RelatedItem);

const List = ({ rooms, parentHouseId }) => {
  const relatedIds = useRelatedRoomIds();

  const hasRelatedFlat = useHasRelatedFlat();

  const currentRelatedIds = useSelector(selectRelatedRoomIds);

  const renderedRooms = rooms.filter((room) => !currentRelatedIds.has(room.id));

  if (!renderedRooms.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Все доступные помещения уже связаны"
      />
    );
  }

  return (
    <ColListWrapper nested>
      {renderedRooms.map((room) => {
        const selected = relatedIds.has(room.id);
        const disabled =
          !selected && room.typeId === FLAT_TYPE_ID && hasRelatedFlat;

        return (
          <MemoizedRelatedItem
            key={room.id}
            room={room}
            selected={selected}
            parentHouseId={parentHouseId}
            disabled={disabled}
          />
        );
      })}
    </ColListWrapper>
  );
};

export default List;

function RelatedItem({ room, selected, parentHouseId, disabled }) {
  const selectIsExcluded = useMemo(
    () => createIsRoomExcludedSelector(room.houseId, room.id),
    [room.houseId, room.id]
  );

  const excluded = useSelector(selectIsExcluded);

  const dispatch = useDispatch();

  const { onChange } = useRelatedRoomsActions();

  const handleClick = () => {
    if (selected) {
      onChange(false, room);
      return;
    }

    if (!excluded) {
      onChange(true, room);
      return;
    }

    Modal.confirm({
      title: 'Помещение исключено из приемки',
      content: 'Вернуть его в приемку и привязать?',
      centered: true,
      onCancel: () => {},
      onOk: () => {
        dispatch(roomExcludeClicked(room, false));
        onChange(true, room);
      },
    });
  };

  return (
    <Tooltip title={disabled ? 'Нельзя связывать более одной квартиры' : ''}>
      <BaseRoomItem
        room={room}
        action={<BaseCheckbox checked={selected} />}
        onClick={handleClick}
        disabled={disabled}
        info={room.houseId !== parentHouseId && <InfoBlock room={room} />}
      />
    </Tooltip>
  );
}

function InfoBlock({ room }) {
  return <span>{createHouseAddress(room)}</span>;
}
